<template>
	<div class>
		<!-- Modal Component -->
		<ModalBox v-model="legacyModalVisible" title="Edit channel" size="sm" mobile-placement="bottom" scroll-body>
			<div class="px-6 pb-6">
				<div class="flex items-center gap-2 mb-4">
					<img v-if="iconSrc" :src="iconSrc" class="size-6 object-contain" alt="">
					<span class="text-base text-surface-text-1">YouTube Events</span>
				</div>

				<Alert v-if="error" type="error" class="mb-3">
					<div>{{ error }}</div>
				</Alert>

				<!-- Event selection: upcoming broadcasts from the connected YouTube account -->
				<div class="field-container">
					<label class="block font-medium text-tiny text-surface-text-1 mb-2">Select Event</label>
					<drop-down-menu ref="eventDropdown" type="flat" listClasses="bg-surface-3 border border-surface-4 rounded-lg mt-1"
						fullWidth :disabled="eventsLoading || processing">
						<template #toggle-button>
							<div class="basis-[90%] max-w-[90%] truncate text-left text-tiny text-surface-text-1">
								{{ eventsLoading ? 'Loading events...' : (selectedEvent?.name || 'Select one') }}
							</div>
							<icon-base class="ml-auto">
								<icon-chevron-down />
							</icon-base>
						</template>

						<template #menu-items>
							<drop-down-menu-item customClasses="!h-9 flex items-center p-2 hover:bg-surface-4 w-full"
								v-for="event in eventList" :key="event.id"
								:class="{ active: selectedEvent && event.id === selectedEvent.id }"
								@click="onSelectEvent(event)">
								<div class="text-tiny text-surface-text-1 max-w-full truncate text-left">
									{{ event.name }}
								</div>
							</drop-down-menu-item>
						</template>
					</drop-down-menu>
					<p v-if="eventsError" class="text-tiny text-danger mt-1 mb-0">{{ eventsError }}</p>
				</div>

				<div class="field-container mt-4">
					<FormInput :label="`Stream Title (${title.length}/${maxLengthTitle})`" v-model="title"
						:maxlength="maxLengthTitle" placeholder="Enter stream title" />
				</div>

				<div class="field-container mt-4">
					<FormText :label="`Stream Description (${description.length}/${maxLengthDescription})`" v-model="description"
						:maxlength="maxLengthDescription" placeholder="Enter stream description" />
				</div>

				<div class="field-container mt-4">
					<label class="block font-medium text-tiny text-surface-text-1 mb-2">Privacy Status</label>
					<drop-down-menu ref="privacyDropdown" type="flat" listClasses="bg-surface-3 border border-surface-4 rounded-lg mt-1"
						fullWidth :disabled="processing">
						<template #toggle-button>
							<div class="text-left text-tiny text-surface-text-1 capitalize">{{ privacyStatus }}</div>
							<icon-base class="ml-auto">
								<icon-chevron-down />
							</icon-base>
						</template>
						<template #menu-items>
							<drop-down-menu-item v-for="status in privacyStatuses" :key="status"
								customClasses="!h-9 flex items-center p-2 hover:bg-surface-4 w-full text-tiny text-surface-text-1 capitalize"
								@click="onPrivacyStatusChanged(status)">
								{{ status }}
							</drop-down-menu-item>
						</template>
					</drop-down-menu>
				</div>

				<div class="field-container mt-4">
					<div class="flex items-center gap-2">
						<FormSwitch v-model="enableAdsOnStream" />
						<div class="flex items-center text-tiny text-surface-text-1">
							Run ads on stream
							<Tooltip content="Enable to insert pre-roll and mid-roll ads">
								<icon-base class="ml-1 cursor-pointer text-surface-6" :width="16" :height="16"><icon-info /></icon-base>
							</Tooltip>
						</div>
					</div>

					<div v-if="enableAdsOnStream" class="mt-3 ml-1 flex flex-col gap-2 text-tiny text-surface-text-1">
						<FormRadio name="editAdFrequency" v-model="adFrequency" inputValue="conservative"
							label="Conservative (few ads)" />
						<FormRadio name="editAdFrequency" v-model="adFrequency" inputValue="balanced" label="Balanced" />
						<FormRadio name="editAdFrequency" v-model="adFrequency" inputValue="aggressive"
							label="Aggressive (more ads)" />
					</div>
				</div>

				<div class="flex flex-col gap-2 mt-6">
					<FormButton type="main" size="md" fullWidth :disabled="!canSave" @click="onSave">
						{{ processing ? 'Saving...' : 'Save destination' }}
					</FormButton>
					<FormButton type="secondary" size="md" fullWidth :disabled="processing" @click="closeModal">
						Cancel
					</FormButton>
				</div>
			</div>
		</ModalBox>
	</div>
</template>

<script>
import _ from 'lodash'
import { useNotification } from '@kyvg/vue3-notification'
import ModalBox from '@/components/modals/ModalBox.vue'
import { legacyModalBridgeMixin } from '@/composables/useLegacyModalBridge'
import IntegrationService from '@/services/IntegrationService'
import StreamService from '@/services/StreamService'
import utils from '@/utils'
import { coloredPlatformIcon } from '@/Utils/platformIcon'
import Alert from '@/components/Atoms/Alert.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormText from '@/components/Atoms/FormText.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import FormRadio from '@/components/Atoms/FormRadio.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconChevronDown from '@/components/icon/IconChevronDown.vue'
import IconInfo from '@/components/icon/IconInfo.vue'

// serviceMeta.subChannel is stored either as a raw broadcast id or as `type@id`
function broadcastIdOf(subChannel) {
	return subChannel ? String(subChannel).split('@').pop() : null
}

export default {
	name: 'EditYoutubeEventModal',
	mixins: [legacyModalBridgeMixin],
	legacyModalId: 'edit-youtube-event-modal',
	components: {
		ModalBox,
		Alert,
		FormButton,
		FormInput,
		FormText,
		FormSwitch,
		FormRadio,
		Tooltip,
		DropDownMenu,
		DropDownMenuItem,
		IconBase,
		IconChevronDown,
		IconInfo,
	},
	props: {
		stream: {
			type: Object,
			required: true
		},
		platform: {
			type: Object,
			required: true
		}
	},
	emits: ['platform-updated'],
	setup() {
		const { notify } = useNotification()
		return { notify }
	},
	data() {
		return {
			eventList: [],
			eventsLoading: false,
			eventsError: null,
			selectedEvent: null,
			title: '',
			description: '',
			privacyStatus: 'public',
			privacyStatuses: ['public', 'unlisted', 'private'],
			enableAdsOnStream: false,
			adFrequency: 'balanced',
			maxLengthTitle: 100,
			maxLengthDescription: 5000,
			processing: false,
			error: null,
		}
	},
	computed: {
		iconSrc() {
			return coloredPlatformIcon(this.platform)
		},
		currentEventId() {
			return broadcastIdOf(this.platform.serviceMeta?.subChannel)
		},
		eventChanged() {
			return !!this.selectedEvent && this.selectedEvent.id !== this.currentEventId
		},
		canSave() {
			return !this.processing && !!this.selectedEvent && !!this.title.trim()
		},
	},
	methods: {
		onLegacyModalShow() {
			this.onInit()
		},
		onLegacyModalHide() {
			this.processing = false
			this.error = null
		},
		onInit() {
			const meta = this.platform.serviceMeta || {}
			this.title = meta.title || ''
			this.description = meta.description || ''
			this.privacyStatus = meta.privacyStatus || 'public'
			this.enableAdsOnStream = !!meta.enableAdsOnStream
			this.adFrequency = meta.adFrequency || 'balanced'
			this.error = null

			// Show the bound event right away, even if YouTube no longer lists it as upcoming
			this.selectedEvent = this.currentEventId
				? { id: this.currentEventId, name: meta.subChannelName || meta.title || 'Current event' }
				: null
			this.loadEvents()
		},
		async loadEvents() {
			this.eventsLoading = true
			this.eventsError = null
			try {
				const events = await IntegrationService.getServiceSubChannels(this.platform.linkedServiceCreds, { isLinkedOAuth: 1 })
				const list = _.isArray(events) ? events : []
				if (this.selectedEvent && !_.find(list, { id: this.selectedEvent.id })) {
					// keep the current event selectable next to "Create New Event"
					list.splice(1, 0, this.selectedEvent)
				}
				this.eventList = list
			} catch (e) {
				this.eventsError = 'Unable to load events from your YouTube account.'
				this.eventList = this.selectedEvent ? [this.selectedEvent] : []
			}
			this.eventsLoading = false
		},
		onSelectEvent(event) {
			this.$refs.eventDropdown.hide(true)
			if (this.selectedEvent && this.selectedEvent.id === event.id) return
			this.selectedEvent = event

			// prefill from the picked broadcast, same as when adding the destination
			if (event.metadata) {
				this.title = event.metadata.title || ''
				this.description = event.metadata.description || ''
			}
		},
		onPrivacyStatusChanged(status) {
			this.privacyStatus = status
			this.$refs.privacyDropdown.hide(true)
		},
		buildMetaUpdates() {
			const metaUpdates = {
				title: this.title.trim(),
				description: this.description,
				privacyStatus: this.privacyStatus,
				enableAdsOnStream: this.enableAdsOnStream,
			}
			if (this.enableAdsOnStream) {
				metaUpdates.adFrequency = this.adFrequency
			}
			return metaUpdates
		},
		async bindSelectedEvent(metaUpdates) {
			const params = {
				// linkedServiceCreds is the platform's link doc, not the raw oauth doc
				isLinkedOAuth: 1,
				forceCreate: 1,
				subChannel: '-@' + this.selectedEvent.id,
				streamTitle: metaUpdates.title,
				streamDescription: metaUpdates.description,
				title: metaUpdates.title,
				description: metaUpdates.description,
				privacyStatus: metaUpdates.privacyStatus,
				enableAdsOnStream: metaUpdates.enableAdsOnStream,
				adFrequency: metaUpdates.adFrequency,
			}
			// keep the event's own schedule instead of letting the BE reset it
			if (this.selectedEvent.scheduleTime) {
				params.scheduleTime = this.selectedEvent.scheduleTime
			}

			const ingest = await IntegrationService.getServiceIngest(this.platform.linkedServiceCreds, params, 'post')
			if (!ingest || !ingest.server) {
				throw new Error('Unable to retrieve the ingest configuration for this event.')
			}

			const address = {
				server: utils.resolveURL(ingest.server, true),
				key: utils.resolveStreamKey(ingest.key, true),
			}
			const subChannel = ingest.subChannelId || this.selectedEvent.id
			await StreamService.setStreamPlatformAddress(this.stream._id, this.platform._id, address, {
				subChannel,
				subChannelName: metaUpdates.title,
			})

			return { ...address, subChannel }
		},
		async onSave() {
			if (!this.canSave) return
			this.processing = true
			this.error = null

			const metaUpdates = this.buildMetaUpdates()
			const updates = {}
			try {
				if (this.eventChanged) {
					const { server, key, subChannel } = await this.bindSelectedEvent(metaUpdates)
					Object.assign(updates, { server, key })
					metaUpdates.subChannel = subChannel
					metaUpdates.subChannelName = metaUpdates.title
				}

				// title/description/privacy/ads go to whichever broadcast is now bound
				await IntegrationService.updateIntegrationMetadata(
					this.platform.linkedServiceCreds,
					_.omit(metaUpdates, ['subChannel', 'subChannelName'])
				)
			} catch (e) {
				this.error = e?.message || 'Unable to update the destination. Please try again.'
				this.processing = false
				return
			}

			if (this.eventChanged && metaUpdates.title !== this.platform.name) {
				// the destination row is labelled with the event it streams to
				try {
					await StreamService.setStreamPlatformName(this.stream._id, this.platform._id, metaUpdates.title)
					updates.name = metaUpdates.title
				} catch (e) {
					console.log('youtube-event-rename-error', e)
				}
			}

			const serviceMeta = _.assign({}, this.platform.serviceMeta, metaUpdates)
			updates.serviceMeta = serviceMeta
			updates.serviceMetaEditor = {
				formProcessing: _.mapValues(serviceMeta, () => false),
				form: _.assign({}, serviceMeta),
				changes: {},
			}
			if (updates.name) updates.editorName = updates.name

			this.$emit('platform-updated', this.platform, updates)
			this.notify({ group: 'success', text: 'Destination successfully updated.' })
			this.processing = false
			this.closeModal()
		},
		closeModal() {
			this.hideLegacyModal()
		},
	},
}
</script>
